import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type {
  LessonDelete,
  LessonEditLessonSlot,
  LessonEditMove,
} from "@shared/models/analytics/lesson"
import { getPossessiveForLesson } from "@shared/models/analytics/lesson"
import type { Course, CourseDateAnnotationSingle } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { partial } from "lodash-es"
import moment from "moment"
import { findCourseDatesOff } from "../../../dates-off-calculator"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { Narrative } from "@shared/actions/create-narrative"

interface Payload {
  cardStackId: string | null
  planbook: Planbook
  /** The off marker. */
  dateAnnotation: CourseDateAnnotationSingle
  /**
   * The note the user left, if any. It rides in this patch rather than a second dispatch because
   * every annotation write recomputes `datesOff` from the course it was handed: dispatched on its
   * own against a course that hasn't seen the off marker yet, it would turn the day back on.
   */
  noteAnnotation?: CourseDateAnnotationSingle | null
  course: Course
  dateString: string
  bumpOrDelete: "BUMP" | "DELETE" | "NO_LESSON"
  bumpOrDeletePatches: Array<PatchModel>
  bumpOrDeleteUndoPatches: Array<PatchModel>
}

export function prepareTurnDateOffAnd(payload: Payload): PreparedAction {
  return {
    name: "TURN_DATE_OFF_AND",
    payload: payload,
    patches: payload.bumpOrDeletePatches.concat(patches(payload)),
    undoPatches: payload.bumpOrDeleteUndoPatches.concat(undoPatches(payload)),
    narrative: partial(narrative, payload),
  }
}

export default prepareTurnDateOffAnd

const addedAnnotations = (payload: Payload): Array<CourseDateAnnotationSingle> =>
  payload.noteAnnotation
    ? [payload.dateAnnotation, payload.noteAnnotation]
    : [payload.dateAnnotation]

let patches = function (payload: Payload): Array<PatchModel> {
  let added = addedAnnotations(payload)
  let newAnnotations = payload.course.attributes.calendar.dateAnnotations.concat(added)
  let allDatesOff = findCourseDatesOff(newAnnotations)
  let patch = Patch.create("course", payload.course.id)
    .push("attributes.calendar.dateAnnotations", ["$each", added])
    .set("attributes.calendar.datesOff", allDatesOff)
    .inc("attributes.calendar.version", 1)

  return [patch.patch]
}

let undoPatches = function (payload: Payload): Array<PatchModel> {
  let patch = Patch.create("course", payload.course.id)
    .pullAll("attributes.calendar.dateAnnotations", addedAnnotations(payload))
    .set(
      "attributes.calendar.datesOff",
      JSON.parse(JSON.stringify(payload.course.attributes.calendar.datesOff || []))
    )
    .inc("attributes.calendar.version", 1)

  return [patch.patch]
}

export function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let formattedDate = moment(payload.dateString).format("MMMM D, YYYY")
  let eventTitleForAnalytics = "Lesson - Toggle Off - No Lesson"

  let adverbialObjectSpecifierOptions: Record<
    Payload["bumpOrDelete"],
    LessonEditLessonSlot["adverbial_object_specifier"]
  > = {
    BUMP: "off-and-bump",
    DELETE: "off-and-delete",
    NO_LESSON: "off",
  }
  let adverbialObjectSpecifier = adverbialObjectSpecifierOptions[payload.bumpOrDelete]

  let sharedEventProperties: Pick<
    LessonEditLessonSlot,
    | "root_id"
    | "root_type"
    | "date_plan_scheduled_for"
    | "primary_object_id"
    | "primary_object_id_and_ancestor_ids"
    | "primary_possessive"
    | "primary_object_type"
    | "adverbial_possessive"
  > = {
    root_id: payload.planbook.id,
    root_type: "planbook",
    date_plan_scheduled_for: payload.dateAnnotation.attributes.dateString,
    primary_object_id: payload.cardStackId,
    primary_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],
    primary_possessive: getPossessiveForLesson(findFn, userId, [
      payload.planbook.relationships.owner.data.id,
    ]),
    primary_object_type: "lesson",
    adverbial_possessive: getPossessiveForLesson(findFn, userId, [
      payload.planbook.relationships.owner.data.id,
    ]),
  }

  let newEvent: LessonEditLessonSlot = {
    ...createBlankEvent(),
    ...sharedEventProperties,
    primary_verb: "edit",
    adverbial_verb: "toggling",
    adverbial_object_type: "lesson",
    adverbial_object_specifier: adverbialObjectSpecifier,
    adverbial_object_id: payload.cardStackId,
    primary_object_specifier: "lesson_slot",
    adverbial_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],
  }

  let secondEvent: LessonEditMove | LessonDelete | null = null
  if (payload.bumpOrDelete === "BUMP" && payload.cardStackId !== null) {
    eventTitleForAnalytics = "Lesson - Toggle Off - Bump Lesson"
    secondEvent = {
      ...createBlankEvent(),
      ...sharedEventProperties,
      primary_verb: "move",
      primary_object_specifier: null,
      adverbial_verb: "bumping",
      adverbial_object_type: "lesson",
      adverbial_object_specifier: "forward",
      adverbial_object_id: payload.cardStackId,
      adverbial_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],
    }
  } else if (payload.bumpOrDelete === "DELETE" && payload.cardStackId !== null) {
    eventTitleForAnalytics = "Lesson - Toggle Off - Delete Lesson"
    secondEvent = {
      ...createBlankEvent(),
      ...sharedEventProperties,
      primary_verb: "delete",
      primary_object_id: payload.cardStackId,
      primary_object_specifier: null,
      adverbial_verb: "removing",
      adverbial_object_type: "lesson",
      adverbial_object_specifier: "content",
      adverbial_object_id: null,
      adverbial_object_id_and_ancestor_ids: null,
    }
  } else {
    null
  }

  return {
    title: `Turned Class Off`,
    titleForAnalytics: eventTitleForAnalytics,
    description: `Turned ${payload.course.attributes.title} off on ${formattedDate}`,
    activity: {},
    context: {
      dateString: payload.dateString,
      courseId: payload.course.id,
      courseTitle: payload.course.attributes.title,
    },
    newAnalyticsEvents: secondEvent ? [newEvent, secondEvent] : [newEvent],
  }
}
